/**
 * Intro Request view - declarative React component.
 * 
 * Elm-ish architecture: This component is THIN.
 * - No business logic
 * - No state mutations
 * - No API calls
 * - Only renders based on model state
 * - Delegates all actions to callbacks from the program
 * 
 * Safety doctrine:
 * - No direct contact details collected
 * - No legal name, phone, email, address
 * - No employer, income, net worth
 * - No social media handles
 * - Optional short message only
 * - Required acknowledgement checkbox
 */
import { Link } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import type { IntroRequestModel } from './model';
import { MAX_MESSAGE_LENGTH } from './model';

/**
 * Props for the view component.
 */
export interface IntroRequestViewProps {
  /** Current model state */
  model: IntroRequestModel;
  /** Start the intro request flow */
  onStart: (cardId: string | null) => void;
  /** User edited the message */
  onMessageEdit: (message: string) => void;
  /** User toggled acknowledgement */
  onAcknowledgementToggle: (checked: boolean) => void;
  /** User clicked submit */
  onSubmit: () => void;
  /** User clicked retry from error */
  onRetry: () => void;
  /** User clicked back from error */
  onBack: () => void;
}

/**
 * Loading state component.
 */
function LoadingState() {
  return (
    <div className="intro-loading" data-testid="intro-loading">
      <p>Loading...</p>
    </div>
  );
}

/**
 * No card state - invalid or missing cardId.
 */
function NoCardState() {
  return (
    <div className="intro-error" data-testid="intro-no-card">
      <div className="error-icon">⚠</div>
      <h2 className="error-title">Invalid Request</h2>
      <p className="error-message">
        No card was selected for this introduction request.
      </p>
      <p className="error-note">
        Please select a card from the discovery page.
      </p>
      <div className="error-actions">
        <Link to={RoutePaths.DISCOVER} className="action-link">
          Go to Discovery
        </Link>
      </div>
    </div>
  );
}

/**
 * Duplicate request state.
 */
function DuplicateState({ cardTitle }: { cardTitle: string }) {
  return (
    <div className="intro-duplicate" data-testid="intro-duplicate">
      <div className="duplicate-icon">⏱</div>
      <h2 className="duplicate-title">Request Already Submitted</h2>
      <p className="duplicate-message">
        An intro request for <strong>{cardTitle}</strong> has already been received.
      </p>
      <p className="duplicate-note">
        Please wait for review. You will be notified of any updates.
      </p>
      <div className="duplicate-actions">
        <Link to={RoutePaths.DISCOVER} className="action-link">
          Back to Discovery
        </Link>
      </div>
    </div>
  );
}

/**
 * Error state for submission failures.
 */
function ErrorState({ message, onRetry, onBack }: { message: string; onRetry: () => void; onBack: () => void }) {
  return (
    <div className="intro-error" data-testid="intro-error">
      <div className="error-icon">⚠</div>
      <h2 className="error-title">Submission Failed</h2>
      <p className="error-message">{message}</p>
      <div className="error-actions">
        <button onClick={onRetry} className="retry-button">
          Try Again
        </button>
        <Link to={RoutePaths.DISCOVER} className="action-link">
          Back to Discovery
        </Link>
      </div>
    </div>
  );
}

/**
 * Success state after submission.
 */
function SuccessState({ cardTitle }: { cardTitle: string }) {
  return (
    <div className="intro-success" data-testid="intro-success">
      <div className="success-icon">✓</div>
      <h2 className="success-title">Request Received</h2>
      <p className="success-message">
        Your introduction request for <strong>{cardTitle}</strong> has been submitted.
      </p>
      <p className="success-note">
        You will be notified if the candidate accepts. 
        Do not attempt direct contact.
      </p>
      <div className="success-actions">
        <Link to={RoutePaths.DISCOVER} className="action-link">
          Back to Discovery
        </Link>
      </div>
    </div>
  );
}

/**
 * Card preview component.
 */
function CardPreview({ cardId, displayTitle }: { cardId: string; displayTitle: string }) {
  return (
    <div className="card-preview" data-testid="card-preview">
      <h3 className="preview-label">Requesting intro to:</h3>
      <p className="preview-title" data-testid="preview-title">{displayTitle}</p>
      <p className="preview-id">Card ID: {cardId}</p>
    </div>
  );
}

/**
 * Safety notice for intro request.
 */
function SafetyNotice() {
  return (
    <div className="safety-notice" data-testid="safety-notice">
      <h3 className="notice-title">Safety Guidelines</h3>
      <ul className="notice-list">
        <li>This is a request only. Acceptance is not guaranteed.</li>
        <li>Do not include direct contact details (email, phone, address).</li>
        <li>Do not include social media handles or links.</li>
        <li>Do not include personal identity information.</li>
        <li>Platform safety review may apply to all requests.</li>
        <li>Requests are identity-minimized for your protection.</li>
      </ul>
    </div>
  );
}

/**
 * Intro request form component.
 * Pure view - all logic delegated to program.
 */
function IntroRequestForm({
  model,
  onMessageEdit,
  onAcknowledgementToggle,
  onSubmit,
}: {
  model: IntroRequestModel;
  onMessageEdit: (message: string) => void;
  onAcknowledgementToggle: (checked: boolean) => void;
  onSubmit: () => void;
}) {
  const isSubmitting = model.status === 'submitting';
  const isValid = model.acknowledgementChecked && model.message.length <= MAX_MESSAGE_LENGTH;
  
  return (
    <form className="intro-form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }} data-testid="intro-form">
      {model.card && (
        <CardPreview cardId={model.card.id} displayTitle={model.card.displayTitle} />
      )}
      
      <SafetyNotice />
      
      <div className="form-field">
        <label htmlFor="message" className="field-label">
          Add a note (optional)
        </label>
        <textarea
          id="message"
          className="field-input"
          value={model.message}
          onChange={(e) => onMessageEdit(e.target.value)}
          placeholder="Why are you interested in connecting? What do you have in common?"
          maxLength={MAX_MESSAGE_LENGTH}
          rows={4}
          disabled={isSubmitting}
          data-testid="message-input"
        />
        <div className="field-hint" data-testid="message-counter">
          {model.message.length}/{MAX_MESSAGE_LENGTH} characters
        </div>
      </div>
      
      <div className="form-field checkbox-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={model.acknowledgementChecked}
            onChange={(e) => onAcknowledgementToggle(e.target.checked)}
            className="checkbox-input"
            disabled={isSubmitting}
            data-testid="acknowledgement-checkbox"
          />
          <span className="checkbox-text">
            I understand this is a request only, I will not include direct contact details,
            and platform safety review may apply.
          </span>
        </label>
      </div>
      
      <div className="form-actions">
        <button
          type="submit"
          className="submit-button"
          disabled={!isValid || isSubmitting}
          data-testid="submit-button"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Request'}
        </button>
        <Link to={RoutePaths.DISCOVER} className="cancel-link">
          Cancel
        </Link>
      </div>
    </form>
  );
}

/**
 * Loading state while submitting.
 */
function SubmittingState() {
  return (
    <div className="intro-loading" data-testid="intro-submitting">
      <p>Submitting your request...</p>
    </div>
  );
}

/**
 * Main view component - routes to appropriate sub-component based on status.
 * 
 * This is the ONLY React component in the feature.
 * All logic is in the program hook; this just renders.
 */
export function IntroRequestView(props: IntroRequestViewProps) {
  const { model, onMessageEdit, onAcknowledgementToggle, onSubmit, onRetry } = props;
  
  // Loading state when fetching card data
  if (model.status === 'loading') {
    return <LoadingState />;
  }
  
  // No card provided or card not found
  if (model.status === 'no_card') {
    return <NoCardState />;
  }
  
  // Duplicate request detected
  if (model.status === 'duplicate' && model.card) {
    return <DuplicateState cardTitle={model.card.displayTitle} />;
  }
  
  // Submission failed
  if (model.status === 'error') {
    return (
      <ErrorState 
        message={model.error || 'An error occurred'} 
        onRetry={onRetry}
        onBack={onRetry}
      />
    );
  }
  
  // Successfully submitted
  if (model.status === 'success' && model.card) {
    return <SuccessState cardTitle={model.card.displayTitle} />;
  }
  
  // Submitting state
  if (model.status === 'submitting') {
    return (
      <>
        {model.card && (
          <CardPreview cardId={model.card.id} displayTitle={model.card.displayTitle} />
        )}
        <SubmittingState />
      </>
    );
  }
  
  // Form state (default)
  return (
    <IntroRequestForm
      model={model}
      onMessageEdit={onMessageEdit}
      onAcknowledgementToggle={onAcknowledgementToggle}
      onSubmit={onSubmit}
    />
  );
}